import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSession } from '../state/useSession';
import { newAppPath } from '../state/builderMode';
import { useProjects } from '../state/projects';
import { isUnfinished, isWebApp } from '@/webapp/webAppRecord';
import { siteLink } from '@/webapp/siteAddress';
import { timeAgo } from '@/utils/time';
import type { AppDefinition } from '@/types/app';
import { EmptyState, FAB, Screen } from '../ui';
import ProjectCard from './ProjectCard';

/** Ported from src/app/(main)/(tabs)/index.tsx — the projects you are building. */
export default function Home() {
  const navigate = useNavigate();
  const { user } = useSession();
  const projects = useProjects((s) => s.projects);
  const isLoading = useProjects((s) => s.isLoading);

  useEffect(() => {
    void useProjects.getState().loadProjects(user?.id ?? '');
  }, [user?.id]);

  return (
    <Screen title="Build">
      {projects.length === 0 && !isLoading ? (
        <EmptyState
          icon="emptyApps"
          title="No projects yet"
          body="Describe an app in a sentence and Saddle builds it."
          ctaTitle="Create an app"
          onCta={() => navigate(newAppPath())}
        />
      ) : (
        <ul className="project-list app-grid">
          {projects.map((p) => {
            const definition = p.definition as AppDefinition;
            // Where the page lives; null for an app with no page, or a build that did not land.
            const site = siteLink(definition);
            const unfinished = isUnfinished(definition);
            return (
              <ProjectCard
                key={p.id}
                name={p.name}
                onOpenEditor={() => navigate(`/project/${p.id}`)}
                href={site?.url}
                address={site?.address}
                meta={
                  /* A build that did not land keeps its row; this says why it is there. */
                  unfinished ? (
                    <span className="project-warn">Not built yet</span>
                  ) : !isWebApp(definition) ? (
                    `Phone only · ${timeAgo(p.updated_at)}`
                  ) : (
                    timeAgo(p.updated_at)
                  )
                }
              />
            );
          })}
        </ul>
      )}
      <FAB icon="add" onPress={() => navigate(newAppPath())} accessibilityLabel="New app" />
    </Screen>
  );
}
